import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Pressable from '@atlaskit/primitives/pressable';
import { auth, AuthError } from '@atlaskit/outbound-auth-flow-client';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Lozenge from '@atlaskit/lozenge';

import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { LoadableAfterPaint } from '@confluence/loadable';
import { usePageSpaceKey } from '@confluence/page-context';
import { isPersonalSpace } from '@confluence/space-utils/entry-points/isPersonalSpace.ts';

import { CloseMenu } from './CloseMenu.tsx';
import { useDismissCuration } from './hooks/useDismissCuration.ts';
import { StackedContentIcons } from './StackedContentIcons.tsx';
import {
	AUTH_OPTIONS,
	EXTERNAL_PRODUCT_CONNECTION,
	type AuthenticationStatus,
	type ExternalProductConnection,
} from './__types__/RemoteObject.ts';

const ThirdPartyAuthenticationModal = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ThirdPartyAuthenticationModal" */ './ThirdPartyAuthenticationModal.tsx'
			)
		).ThirdPartyAuthenticationModal,
});

const styles = cssMap({
	container: {
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.small'),
		marginTop: token('space.100'),
		overflow: 'hidden',
		padding: token('space.150'),
		position: 'relative',
	},
	topRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		marginBottom: token('space.050'),
	},
	mainAction: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.050'),
		backgroundColor: 'transparent',
		padding: token('space.0'),
		width: '100%',
		textAlign: 'start',
	},
});

const i18n = defineMessages({
	title: {
		id: 'space-curation.space-navigation.third-party-auth.title',
		defaultMessage: 'Make the most of docs, sheets, slides, and folders',
		description:
			'Title for the third-party authentication entry point card, specifically about google drive',
	},
	titleGoogleDrive: {
		id: 'space-curation.space-navigation.third-party-auth.title-google-drive',
		defaultMessage: 'Connect to Google Drive to bring content into your space.',
		description:
			'Body text for the third-party auth entry point card when only Google Drive is unconnected',
	},
	titleMicrosoft: {
		id: 'space-curation.space-navigation.third-party-auth.title-microsoft',
		defaultMessage: 'Connect to Microsoft to bring content into your space.',
		description:
			'Body text for the third-party auth entry point card when only Microsoft is unconnected',
	},
	titleMulti: {
		id: 'space-curation.space-navigation.third-party-auth.title-multi',
		defaultMessage: 'Connect to apps to bring content into your space.',
		description:
			'Body text for the third-party auth entry point card when multiple apps are unconnected',
	},
	beta: {
		id: 'space-curation.space-navigation.third-party-auth.beta',
		defaultMessage: 'Beta',
		description: 'Beta lozenge label displayed next to the third-party auth entry point title',
	},
	connect: {
		id: 'space-curation.space-navigation.third-party-auth.connect-apps',
		defaultMessage: 'Connect apps',
		description:
			'Label for the connect link in the third-party auth entry point card. It opens a modal where users can choose an app to connect',
	},
	connectProvider: {
		id: 'space-curation.space-navigation.third-party-auth.connect-provider',
		defaultMessage: 'Connect {providerName}',
		description:
			'Label for the connect link when there is only one unconnected app, directly initiating auth for that specific provider',
	},
	successFlagTitle: {
		id: 'space-curation.space-navigation.third-party-auth.success-title-v2',
		defaultMessage: '{providerName} connected',
		description:
			'Title for the success flag shown after a user successfully authenticates with a third-party provider',
	},
	successFlagDescription: {
		id: 'space-curation.space-navigation.third-party-auth.success-description',
		defaultMessage: "We're on it! Rovo is looking for content for this space, check back later.",
		description:
			'Description for the success flag shown after a user successfully authenticates with a third-party provider',
	},
	cancelledFlagTitle: {
		id: 'space-curation.space-navigation.third-party-auth.cancelled-title',
		defaultMessage: 'Connection cancelled',
		description:
			'Title for the warning flag shown when the user closes the auth popup without completing authentication',
	},
	cancelledFlagDescription: {
		id: 'space-curation.space-navigation.third-party-auth.cancelled-description-v2',
		defaultMessage: "Couldn't connect {providerName}. Please try again.",
		description:
			'Description for the warning flag shown when the user closes the auth popup without completing authentication',
	},
	errorFlagTitle: {
		id: 'space-curation.space-navigation.third-party-auth.error-title',
		defaultMessage: 'Connection failed',
		description:
			'Title for the error flag shown when third-party authentication fails unexpectedly',
	},
	errorFlagDescription: {
		id: 'space-curation.space-navigation.third-party-auth.error-description-v2',
		defaultMessage: 'Something went wrong connecting {providerName}. Please try again.',
		description:
			'Description for the error flag shown when third-party authentication fails unexpectedly',
	},
});

type ThirdPartyAuthenticationProps = {
	unauthenticatedServices: Partial<Record<ExternalProductConnection, AuthenticationStatus>>;
	onThirdPartyAuthRefetch: () => void | Promise<void>;
	onAuthSuccess?: (product: ExternalProductConnection) => void;
	spaceId?: string;
	mountTimestamp: number;
};

export const ThirdPartyAuthentication = ({
	unauthenticatedServices,
	onThirdPartyAuthRefetch,
	onAuthSuccess,
	spaceId,
	mountTimestamp,
}: ThirdPartyAuthenticationProps) => {
	const intl = useIntl();
	const [isVisible, setIsVisible] = useState(true);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const hasFiredRenderedRef = useRef(false);
	const [spaceKey] = usePageSpaceKey();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { dismissAction } = useDismissCuration();

	const eligibleConnectors = useMemo(
		() =>
			AUTH_OPTIONS.filter(
				(option) =>
					unauthenticatedServices[option] &&
					Boolean(unauthenticatedServices[option]?.outboundAuthUrl),
			),
		[unauthenticatedServices],
	);

	const isRendered = !isPersonalSpace(spaceKey) && isVisible;

	useEffect(() => {
		if (isRendered && spaceId && !hasFiredRenderedRef.current) {
			hasFiredRenderedRef.current = true;
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'rendered',
					actionSubject: 'curationEntry',
					source: 'containerNavigation',
					attributes: {
						spaceId,
						spaceKey,
						entryPoint: 'authEntry',
						suggestionCount: 0,
						eligibleConnectors,
						renderLatencyMs: Date.now() - mountTimestamp,
					},
				},
			}).fire();
		}
	}, [isRendered, spaceId, spaceKey, eligibleConnectors, createAnalyticsEvent, mountTimestamp]);

	const handleDismiss = () => {
		if (spaceId) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'dismissed',
					actionSubject: 'curationEntry',
					source: 'containerNavigation',
					attributes: {
						spaceId,
						spaceKey,
						entryPoint: 'authEntry',
						dismissMethod: 'close_button',
					},
				},
			}).fire();
		}
		void dismissAction(false);
		setIsVisible(false);
	};

	const handleNeverShowAgain = () => {
		if (spaceId) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'dismissed',
					actionSubject: 'curationEntry',
					source: 'containerNavigation',
					attributes: {
						spaceId,
						spaceKey,
						entryPoint: 'authEntry',
						dismissMethod: 'never_show_again',
					},
				},
			}).fire();
		}
		void dismissAction(true);
		setIsVisible(false);
	};

	if (isPersonalSpace(spaceKey) || !isVisible || eligibleConnectors.length === 0) {
		return null;
	}

	const getProviderName = (option: (typeof AUTH_OPTIONS)[number]): string => {
		switch (option) {
			case EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE:
				return 'Google Drive';
			case EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS:
				return 'Microsoft';
			default:
				return option;
		}
	};

	const handleConnect = async (
		option: (typeof AUTH_OPTIONS)[number],
		flags: FlagsStateContainer,
	) => {
		const authUrl = unauthenticatedServices[option]?.outboundAuthUrl;
		if (!authUrl) {
			return;
		}
		const providerName = getProviderName(option);
		try {
			await auth(authUrl);
			onAuthSuccess?.(option);
			await onThirdPartyAuthRefetch();
			void flags?.showSuccessFlag({
				id: 'space-curation.third-party-auth.success',
				title: intl.formatMessage(i18n.successFlagTitle, { providerName }),
				description: intl.formatMessage(i18n.successFlagDescription),
				isAutoDismiss: true,
			});
		} catch (error) {
			if (error instanceof AuthError && error.type === 'auth_window_closed') {
				void flags?.showWarningFlag({
					id: 'space-curation.third-party-auth.cancelled',
					title: intl.formatMessage(i18n.cancelledFlagTitle),
					description: intl.formatMessage(i18n.cancelledFlagDescription, { providerName }),
					isAutoDismiss: true,
				});
			} else {
				void flags?.showErrorFlag({
					id: 'space-curation.third-party-auth.error',
					title: intl.formatMessage(i18n.errorFlagTitle),
					description: intl.formatMessage(i18n.errorFlagDescription, { providerName }),
					isAutoDismiss: true,
				});
			}
		}
	};

	const singleConnector = eligibleConnectors.length === 1 ? eligibleConnectors[0] : undefined;

	const CONNECTOR_UI: Record<
		(typeof AUTH_OPTIONS)[number],
		{
			singleIcon: 'google-drive' | 'onedrive';
			titleMessage: string;
		}
	> = {
		[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE]: {
			singleIcon: 'google-drive',
			titleMessage: intl.formatMessage(i18n.titleGoogleDrive),
		},
		[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS]: {
			singleIcon: 'onedrive',
			titleMessage: intl.formatMessage(i18n.titleMicrosoft),
		},
	};

	const singleIcon = singleConnector ? CONNECTOR_UI[singleConnector].singleIcon : undefined;
	const titleText = singleConnector
		? CONNECTOR_UI[singleConnector].titleMessage
		: intl.formatMessage(i18n.titleMulti);
	const connectText = singleConnector
		? intl.formatMessage(i18n.connectProvider, { providerName: getProviderName(singleConnector) })
		: intl.formatMessage(i18n.connect);

	return (
		<Subscribe to={[FlagsStateContainer]}>
			{(flags: FlagsStateContainer) => (
				<>
					<Box xcss={styles.container} testId="space-navigation-third-party-auth">
						<Box xcss={styles.topRow}>
							<Pressable
								xcss={styles.mainAction}
								onClick={() => {
									if (singleConnector) {
										void handleConnect(singleConnector, flags);
									} else {
										setIsModalOpen(true);
									}
								}}
							>
								<Box>
									<StackedContentIcons singleIcon={singleIcon} />
									<Text color="color.text">
										{titleText}{' '}
										<Lozenge appearance="accent-gray">{intl.formatMessage(i18n.beta)}</Lozenge>
									</Text>
								</Box>
								<Text color="color.link">{connectText}</Text>
							</Pressable>
							<CloseMenu onDismiss={handleDismiss} onNeverShowAgain={handleNeverShowAgain} />
						</Box>
					</Box>
					{isModalOpen && (
						<ThirdPartyAuthenticationModal
							unauthenticatedServices={unauthenticatedServices}
							onClose={() => setIsModalOpen(false)}
							onConnect={(option) => void handleConnect(option, flags)}
						/>
					)}
				</>
			)}
		</Subscribe>
	);
};
